<template>
  <div class="product-setup">
    <h2 class="section-heading">
      Your Product Setup
    </h2>

    <div class="setup-card">
      <div class="image-panel">
        <div
          v-if="hasImage"
          class="image-preview"
        >
          <img
            :src="imageUrl"
            alt="Uploaded product reference"
          >
        </div>
        <div
          v-else
          class="image-placeholder"
          @click="triggerUpload"
        >
          <div class="placeholder-icon">
            <span class="upload-icon">+</span>
          </div>
          <p class="placeholder-title">
            Upload image
          </p>
        </div>

        <div
          v-if="hasImage"
          class="image-actions"
        >
          <b-button
            variant="outline-secondary"
            class="action-button"
            :disabled="!hasImage"
            size="sm"
            @click="resetCrop"
          >
            <feather-icon
              icon="CropIcon"
              size="14"
              class="action-icon"
            />
            <span>Reset crop</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            class="action-button"
            size="sm"
            @click="changeImage"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="action-icon"
            />
            <span>{{ hasImage ? 'Change image' : 'Upload image' }}</span>
          </b-button>
        </div>
      </div>

      <div class="prompt-panel">
        <p class="prompt-label">
          Image to image Prompt
        </p>

        <span class="prompt-label font-small">Select a suggestion to quickly generate your image prompt</span>
        <div class="textarea-container">
          <!-- Tags Display -->
          <div
            v-if="selectedSuggestions.length > 0"
            class="suggestion-tags"
          >
            <div
              v-for="(suggestion, index) in selectedSuggestions"
              :key="`${suggestion._id}-${index}`"
              class="suggestion-tag"
            >
              <span class="tag-text">{{ suggestion.value }}</span>
              <feather-icon
                icon="XIcon"
                size="14"
                class="tag-remove-icon"
                @click="removeSuggestion(index)"
              />
            </div>
          </div>

          <!-- Textarea (hidden when tags are present) -->
          <b-form-textarea
            v-model="prompt"
            :maxlength="promptLimit"
            :disabled="true"
            rows="10"
            placeholder="Please use suggestions to generate a prompt"
            class="prompt-textarea"
            :class="{ 'hidden': selectedSuggestions.length > 0 }"
          />
          <div class="prompt-actions">
            <b-button
              variant="outline-secondary"
              class="suggestion-button-inline"
              :disabled="isGeneratingPrompt || isGeneratingImage"
              @click="requestSuggestion"
            >
              <feather-icon
                icon="ZapIcon"
                size="16"
                class="suggestion-icon"
              />
              <span>Suggestion</span>
            </b-button>
            <div class="bottom-right-controls">
              <span class="character-count">{{ characterCount }}/{{ promptLimit }}</span>
              <!-- <b-button
                v-if="hasImage"
                variant="primary"
                size="sm"
                class="ai-prompt-button"
                :disabled="isGeneratingPrompt || isGeneratingImage"
                @click="generatePrompt"
              >
                <feather-icon
                  :icon="isGeneratingPrompt ? 'LoaderIcon' : 'ZapIcon'"
                  size="14"
                  :class="{ 'spinning': isGeneratingPrompt }"
                  class="ai-icon"
                />
                <span>AI Prompt</span>
              </b-button> -->
            </div>
          </div>
        </div>
        <b-button
          variant="primary"
          class="generate-button"
          :disabled="!canGenerate || isGeneratingImage"
          @click="generateImage"
        >
          <feather-icon
            :icon="isGeneratingImage ? 'LoaderIcon' : 'SparklesIcon'"
            size="18"
            class="generate-icon"
            :class="{ 'spinning': isGeneratingImage }"
          />
          <span>{{ isGeneratingImage ? 'Generating...' : 'Generate image' }}</span>
        </b-button>
      </div>
    </div>

    <input
      ref="imageInput"
      type="file"
      accept="image/*"
      class="hidden-input"
      @change="onFileChange"
    >

    <b-modal
      v-model="showAdjustModal"
      id="adjust-image-modal"
      title="Adjust your image to 9:16"
      size="lg"
      hide-footer
      centered
      no-close-on-backdrop
      @hidden="onModalHidden"
    >
      <div class="adjust-modal modal-actions">
        <div
          v-if="previewUrl"
          class="toolbar"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="isProcessing"
            @click="resetCropInternal"
          >
            Reset
          </b-button>
        </div>

        <div class="cropper-wrapper">
          <Cropper
            v-if="previewUrl"
            ref="cropper"
            class="cropper"
            :src="previewUrl"
            :stencil-props="{ aspectRatio: 9 / 16 }"
          />
        </div>

        <div class="modal-actions">
          <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="isProcessing"
            @click="closeAdjustModal"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            class="primary-gradient-btn"
            :disabled="isProcessing || !selectedFile"
            @click="confirmAdjust"
          >
            Confirm
          </b-button>
        </div>
      </div>
    </b-modal>

    <SuggestionModal
      ref="suggestionModal"
      :show="showSuggestionModal"
      :image-url="imageUrl"
      :current-selected-suggestions="selectedSuggestions"
      @close="handleSuggestionModalClose"
      @apply-suggestion="handleApplySuggestion"
    />
  </div>
</template>

<script>
import { BButton, BFormTextarea, BModal } from 'bootstrap-vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { mapActions, mapGetters } from 'vuex'
import heic2any from 'heic2any'
import SuggestionModal from './SuggestionModal.vue'

export default {
  name: 'ImageProductSetup',
  components: {
    BButton,
    BFormTextarea,
    BModal,
    Cropper,
    SuggestionModal,
  },
  props: {
    imageUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      promptLimit: 1000,
      prompt: '',
      selectedPromptIds: [],
      selectedSuggestions: [], // Store selected suggestions as objects
      showSuggestionModal: false,
      isGeneratingPrompt: false,
      showAdjustModal: false,
      previewUrl: '',
      originalUrl: '',
      selectedFile: null,
      originalFile: null,
      isProcessing: false,
      uploadedImageUrl: '',
    }
  },
  computed: {
    ...mapGetters('videoGeneration', ['imageGenerationProcessLoading', 'promptSuggestionsData']),
    characterCount() {
      return this.prompt.length
    },
    hasImage() {
      return Boolean(this.imageUrl)
    },
    canGenerate() {
      return Boolean(this.hasImage && this.prompt.trim())
    },
    isGeneratingImage() {
      return this.imageGenerationProcessLoading
    },
    categorizedSuggestions() {
      // Get suggestions data from Vuex store
      if (!this.promptSuggestionsData) return {}

      // The API response has keys like 'action', 'person', 'region', 'stype', 'background'
      const order = ['person', 'region', 'background', 'action', 'stype']
      const sortedKeys = Object.keys(this.promptSuggestionsData).sort((a, b) => {
        const indexA = order.indexOf(a)
        const indexB = order.indexOf(b)
        if (indexA === -1 && indexB === -1) return a.localeCompare(b)
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
      })

      const categorized = {}
      sortedKeys.forEach(key => {
        if (this.promptSuggestionsData[key]) {
          categorized[key] = this.promptSuggestionsData[key]
        }
      })
      return categorized
    },
  },
  watch: {
    prompt(newValue) {
      const sanitized = newValue.slice(0, this.promptLimit)
      if (sanitized !== newValue) {
        this.prompt = sanitized
        return
      }
      this.$emit('prompt-change', sanitized)
    },
  },
  beforeDestroy() {
    this.revokePreview()
  },
  methods: {
    ...mapActions('videoGeneration', ['generateImagePrompt', 'uploadImage']),
    triggerUpload() {
      const input = this.$refs.imageInput
      if (input) input.click()
    },
    openImagePicker() {
      this.triggerUpload()
    },
    changeImage() {
      this.triggerUpload()
    },
    resetCrop() {
      if (!this.selectedFile) return
      if (this.previewUrl && this.previewUrl !== this.originalUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = this.originalUrl
      if (!this.previewUrl) return
      this.showAdjustModal = true
      this.$nextTick(() => {
        const { cropper } = this.$refs
        if (cropper && typeof cropper.reset === 'function') cropper.reset()
      })
    },
    resetCropInternal() {
      const { cropper } = this.$refs
      if (cropper && typeof cropper.reset === 'function') cropper.reset()
    },
    async onFileChange(event) {
      const file = event?.target?.files?.[0]
      if (!file || !file.type.startsWith('image/')) {
        this.$toast.error('Please select a valid image file')
        return
      }

      this.revokePreview()
      this.selectedFile = file
      this.originalFile = file

      try {
        let processedFile = file

        // Check if the file is HEIC/HEIF format
        const isHeicFile = file.type === 'image/heic'
          || file.type === 'image/heif'
          || file.name.toLowerCase().endsWith('.heic')
          || file.name.toLowerCase().endsWith('.heif')

        if (isHeicFile) {
          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.92,
          })

          // heic2any returns an array, get the first element
          const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob

          // Create a new File object with the converted blob
          const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
          processedFile = new File([jpegBlob], fileName, { type: 'image/jpeg' })
          this.selectedFile = processedFile
          this.originalFile = processedFile
        }

        const blobUrl = URL.createObjectURL(processedFile)
        this.previewUrl = blobUrl
        this.originalUrl = blobUrl
        this.showAdjustModal = true
      } catch (error) {
        this.$toast.error('Failed to process image. Please try a different file.')
      }
    },
    closeAdjustModal() {
      this.showAdjustModal = false
    },
    async cropImage(closeAfter) {
      const { cropper } = this.$refs
      if (!cropper) return
      try {
        this.isProcessing = true
        const result = cropper.getResult()
        if (!result || !result.canvas) throw new Error('Unable to crop image')
        const blob = await new Promise(resolve => result.canvas.toBlob(resolve, 'image/jpeg', 0.92))
        if (!blob) throw new Error('Failed generating image blob')
        const fileName = this.selectedFile?.name || 'cropped.jpg'
        const croppedFile = new File([blob], fileName, { type: 'image/jpeg' })
        if (this.previewUrl && this.previewUrl !== this.originalUrl) {
          URL.revokeObjectURL(this.previewUrl)
        }
        const newUrl = URL.createObjectURL(blob)
        this.selectedFile = croppedFile
        this.previewUrl = newUrl
        if (closeAfter) this.showAdjustModal = false
      } catch (error) {
        this.$toast.error(error?.message || 'Crop failed')
      } finally {
        this.isProcessing = false
      }
    },
    async confirmAdjust() {
      if (!this.selectedFile) {
        this.$toast.error('No image to upload')
        return
      }

      // Close modal immediately to prevent flickering
      this.showAdjustModal = false

      try {
        this.isProcessing = true

        // First apply the crop (combine apply logic)
        await this.cropImage(false)

        // Then upload the cropped image (confirm logic)
        const response = await this.uploadImage({ file: this.selectedFile })
        const url = response?.data?.url || response?.data?.imageUrl || response?.data
        if (!url) throw new Error('No URL returned from server')
        this.uploadedImageUrl = url
        this.$emit('image-uploaded', url)
      } catch (error) {
        this.$toast.error(error?.message || 'Failed to upload image')
      } finally {
        this.isProcessing = false
      }
    },
    revokePreview() {
      if (this.previewUrl && this.previewUrl !== this.originalUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      if (this.originalUrl) {
        URL.revokeObjectURL(this.originalUrl)
      }
      this.previewUrl = ''
      this.originalUrl = ''
    },
    onModalHidden() {
      const input = this.$refs.imageInput
      if (input) input.value = ''
    },
    requestSuggestion() {
      this.showSuggestionModal = true
    },
    applySuggestion(text) {
      this.prompt = (text || '').slice(0, this.promptLimit)
    },
    handleSuggestionModalClose() {
      this.showSuggestionModal = false
    },
    handleApplySuggestion(suggestion) {
      // suggestion is now an object: { promptIds: [...], promptText: '...' }
      if (suggestion.promptText) {
        this.applySuggestion(suggestion.promptText)
      }
      // Store promptIds for use in generateImage
      this.selectedPromptIds = suggestion.promptIds || []

      // Store selected suggestions as objects for tag display
      this.selectedSuggestions = suggestion.promptIds
        ? suggestion.promptIds.map(id => this.findSuggestionById(id)).filter(Boolean)
        : []

      this.showSuggestionModal = false
    },
    findSuggestionById(id) {
      // Find suggestion by ID in all categories
      const categories = Object.values(this.categorizedSuggestions)
      const foundSuggestion = categories.find(category => category.find(s => s._id === id)) // eslint-disable-line no-underscore-dangle
      return foundSuggestion ? foundSuggestion.find(s => s._id === id) : null // eslint-disable-line no-underscore-dangle
    },
    removeSuggestion(index) {
      // Remove suggestion from local array
      this.selectedSuggestions.splice(index, 1)

      // Update promptIds
      this.selectedPromptIds = this.selectedSuggestions.map(s => s._id) // eslint-disable-line no-underscore-dangle

      // Update prompt text
      this.prompt = this.selectedSuggestions.map(s => s.value).join(', ')

      // Update SuggestionModal if it exists
      if (this.$refs.suggestionModal) {
        this.$refs.suggestionModal.updateSelectedSuggestions(this.selectedSuggestions)
      }
    },
    generateImage() {
      if (!this.canGenerate) return

      // Find promptIds based on the current prompt text
      const promptIds = this.findPromptIdsFromText(this.prompt.trim())

      this.$emit('generate-image', {
        prompt: this.prompt.trim(),
        promptIds: promptIds.length > 0 ? promptIds : (this.selectedPromptIds || []),
        imageUrl: this.imageUrl,
      })
      // Clear promptIds after generation
      this.selectedPromptIds = []
    },
    findPromptIdsFromText(promptText) {
      if (!promptText) return []

      // Get all suggestions from all categories
      const allSuggestions = []
      Object.values(this.categorizedSuggestions || {}).forEach(suggestions => {
        allSuggestions.push(...suggestions)
      })

      // Find suggestions that match the prompt text
      const matchingSuggestions = allSuggestions.filter(suggestion => promptText.includes(suggestion.value))

      // Return the IDs of matching suggestions
      return matchingSuggestions.map(suggestion => suggestion._id) // eslint-disable-line no-underscore-dangle
    },
    async generatePrompt() {
      if (!this.imageUrl) {
        this.$toast.warning('Please upload an image first')
        return
      }

      try {
        this.isGeneratingPrompt = true
        const response = await this.generateImagePrompt({ imageUrl: this.imageUrl })
        const promptText = response?.data?.prompt || response?.data || response?.prompt
        if (promptText) {
          this.prompt = String(promptText).slice(0, this.promptLimit)
          this.$toast.success('Prompt generated successfully')
        }
      } catch (error) {
        this.$toast.error('Failed to generate prompt')
        // eslint-disable-next-line no-console
        console.error('[ImageProductSetup] generateImagePrompt failed', error)
      } finally {
        this.isGeneratingPrompt = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.product-setup {
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #ffffff;
  padding: 24px 32px 32px;
  border-radius: 32px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.section-heading {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.setup-card {
  display: flex;
  gap: 40px;
}

.image-panel {
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.image-preview {
  width: 300px;
  height: 300px; // Square container
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: auto;
    height: 100%;
    max-width: 100%;
    object-fit: contain; // Changed to contain to maintain 9:16 aspect ratio within square
    display: block;
  }
}

.image-placeholder {
  width: 300px;
  height: 300px; // Square container to match image-preview
  border-radius: 8px;
  border: 2px dashed #d1d5db;
  background: rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #818cf8;
    color: #4f46e5;
    background: #eef2ff;
  }
}

.placeholder-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(129, 140, 248, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon {
  font-size: 32px;
  line-height: 1;
  color: #6b7280;
  font-weight: 300;
  transition: color 0.3s ease;
}

.placeholder-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.placeholder-subtitle {
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
}

.image-actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #000000;
  font-weight: 400;
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  flex: 1;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: rgba(0, 0, 0, 0.2);
    color: #000000;
  }

  &:focus {
    background: #ffffff;
    border-color: rgba(0, 0, 0, 0.2);
    color: #000000;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.action-icon {
  color: #000000;
}

.prompt-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  .font-small{
    font-size: 14px;
    color: #6b7280;
  }
}

.prompt-label {
  font-size: 16px;
  font-weight: 500;
  color: #000000;
  margin: 0;
  align-self: flex-start;
}

.textarea-container {
  position: relative;
  width: 100%;
}

.suggestion-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: #f9fafb;
  min-height: 268px;
  align-content: flex-start;
}

.suggestion-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(141deg, rgba(129, 255, 244, 0.1) 0%, rgba(110, 68, 255, 0.1) 100%);
  border: 1px solid rgba(129, 255, 244, 0.3);
  border-radius: 16px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  max-width: 100%;
}

.suggestion-tag:hover {
  background: linear-gradient(141deg, rgba(129, 255, 244, 0.15) 0%, rgba(110, 68, 255, 0.15) 100%);
  border-color: rgba(129, 255, 244, 0.5);
  transform: translateY(-1px);
}

.tag-text {
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.tag-remove-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  background: rgba(220, 53, 69, 0.1);
  border: none;
  border-radius: 50%;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tag-remove-icon:hover {
  background: rgba(220, 53, 69, 0.2);
  transform: scale(1.1);
}

.prompt-textarea.hidden {
  display: none;
}

.prompt-textarea {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 10px;
  color: rgba(0, 0, 0, 0.7);
  height: 240px;
  width: 100%;
  resize: none;
  box-shadow: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: rgba(0, 0, 0, 0.2);
    box-shadow: none;
  }
}

.ai-prompt-button {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%);
  border: none;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(141deg, rgba(129, 255, 244, 0.9) 0%, rgba(110, 68, 255, 0.9) 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
}

.ai-icon {
  color: #ffffff;
  &.spinning {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Suggestion button positioned at bottom-left of textarea */
.suggestion-button-inline {
  position: absolute;
  bottom: 10px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #000000;
  font-weight: 400;
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  z-index: 10;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: rgba(0, 0, 0, 0.2);
    color: #000000;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.suggestion-icon {
  color: rgba(0, 0, 0, 0.7);
  width: 16px;
  height: 16px;
}

/* Bottom right controls positioned at bottom-right of textarea */
.bottom-right-controls {
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
}

.character-count {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
}

.generate-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  padding: 6px 16px;
  border: none;
  background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%);
  color: #ffffff;
  font-weight: 500;
  font-size: 14px;
  text-transform: none;
  height: 36px;
  margin-top: 8px;

  &:hover,
  &:focus {
    background: linear-gradient(141deg, rgba(129, 255, 244, 0.9) 0%, rgba(110, 68, 255, 0.9) 100%);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.generate-icon {
  color: inherit;

  &.spinning {
    animation: spin 1s linear infinite;
  }
}

@media (max-width: 1024px) {
  .setup-card {
    flex-direction: column;
    gap: 24px;
  }

  .image-panel {
    align-items: center;
  }

  .image-preview,
  .image-placeholder {
    width: 100%;
    max-width: 300px;
    height: auto;
    aspect-ratio: 1 / 1; // Square aspect ratio for responsive
  }

  .image-actions {
    width: 100%;
  }

  .prompt-panel {
    align-items: stretch;
  }

  .prompt-textarea {
    width: 100%;
  }

  .prompt-toolbar {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .generate-button {
    align-self: center;
  }
}

.hidden-input {
  display: none;
}

.adjust-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .btn {
    border-radius: 20px !important;
  }
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cropper-wrapper {
  width: 100%;
  margin: 0 auto;
}

.applied-image-wrapper {
  max-width: 300px;
  margin: 0 auto;
}

.cropper,
.applied-image {
  width: 100%;
  background: #ddd;
  border-radius: 12px;
  object-fit: cover;
}

.cropper {
  height: 600px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;

  .btn {
    border-radius: 20px !important;
    padding: 8px 16px;
    font-weight: 600;
  }

  .btn-outline-secondary {
    border: 1px solid #6c757d !important;
    color: #6c757d !important;
    background: transparent !important;

    &:hover,
    &:focus,
    &:active {
      background: #6c757d !important;
      color: #ffffff !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
    }
  }

  .primary-gradient-btn {
    background: linear-gradient(135deg, #8ec5fc 0%, #a855f7 100%) !important;
    border: none !important;
    color: #ffffff !important;

    &:hover,
    &:focus,
    &:active {
      background: linear-gradient(135deg, #7bb3f9 0%, #9333ea 100%) !important;
      color: #ffffff !important;
      box-shadow: 0 4px 12px rgba(142, 197, 252, 0.4);
      transform: translateY(-1px);
    }

    &:disabled {
      background: #e5e7eb !important;
      color: #9ca3af !important;
      transform: none;
      box-shadow: none;
    }
  }
}
</style>
